/**
 * Utility functions for managing modals
 */

/**
 * Reset all modals to their default state
 * This is particularly useful when navigating between pages or after a successful payment
 * to ensure no unwanted modals are shown
 * 
 * @param {Function} setModals - The setModals function from Recoil state
 * @param {Function} setOverlay - The setOverlay function from Recoil state
 */
export const resetAllModals = (setModals, setOverlay) => {
  console.log("modalUtils: Resetting all modals to default state");
  
  // Reset all modals to their default state
  setModals(state => ({
    ...state,
    addCard: {visible: false, data: {}},
    addCardMobile: {visible: false, data: {}},
    selectCard: {visible: false},
    selectCardMobile: {visible: false},
    addAddress: {visible: false, data: {}},
    addAddressMobile: {visible: false, data: {}},
    errorCart: {visible: false}, // Explicitly ensure errorCart is closed
    aboutUs: {visible: false},
    aboutUsMobile: {visible: false},
    whatsApp: {visible: false},
    whatsAppMobile: {visible: false},
    bitPay: {visible: false, data: {}},
    bitPayMobile: {visible: false, data: {}},
    applePayMobile: {visible: false, data: {}},
  }));
  
  // Reset overlay state
  setOverlay(false);
};

/**
 * Reset specific modal to its default state
 * 
 * @param {Function} setModals - The setModals function from Recoil state
 * @param {string} modalName - The name of the modal to reset
 */
export const resetModal = (setModals, modalName) => {
  console.log(`modalUtils: Resetting modal ${modalName} to default state`);
  
  setModals(state => ({
    ...state,
    [modalName]: {visible: false, data: state[modalName]?.data || {}},
  }));
};

/**
 * Explicitly close the errorCart modal
 * This is particularly useful during navigation to ensure the error cart doesn't appear
 * 
 * @param {Function} setModals - The setModals function from Recoil state
 */
export const closeErrorCart = (setModals) => {
  console.log("modalUtils: Explicitly closing errorCart modal");
  
  setModals(state => ({
    ...state,
    errorCart: {visible: false},
  }));
};

/**
 * Prepare for navigation to success page
 * This function closes all modals and sets a guard to prevent showing error modals during navigation
 * 
 * @param {Function} setModals - The setModals function from Recoil state
 * @param {Function} setOverlay - The setOverlay function from Recoil state
 */
export const prepareForSuccessNavigation = (setModals, setOverlay) => {
  // Create a visually distinctive log for navigation preparation
  console.log('%c🚀 NAVIGATION PREPARATION STARTED', 
    'background: #d4edda; color: #155724; font-size: 14px; font-weight: bold; padding: 4px;', {
    timestamp: new Date().toISOString(),
    orderId: localStorage.getItem("orderId") || "unknown",
    callerStack: new Error().stack
  });
  
  // Reset all modals
  resetAllModals(setModals, setOverlay);
  
  // Set a guard to prevent showing error modals during navigation
  window.isNavigatingToSuccessPage = true;
  sessionStorage.setItem('isNavigatingToSuccessPage', 'true');
  
  console.log('%c🔒 NAVIGATION GUARD SET', 
    'background: #cce5ff; color: #004085; font-size: 14px; font-weight: bold; padding: 4px;', {
    timestamp: new Date().toISOString(),
    orderId: localStorage.getItem("orderId") || "unknown"
  });
  
  // Reset the guard after navigation should be complete
  setTimeout(() => {
    window.isNavigatingToSuccessPage = false;
    sessionStorage.removeItem('isNavigatingToSuccessPage');
    
    console.log('%c🔓 NAVIGATION GUARD CLEARED', 
      'background: #fff3cd; color: #856404; font-size: 14px; font-weight: bold; padding: 4px;', {
      timestamp: new Date().toISOString(),
      orderId: localStorage.getItem("orderId") || "unknown"
    });
  }, 8000); // Increased from 5000 to 8000 ms to ensure navigation completes
};
